/**
 * JourneyOriginMarker — Apr-2026 design refresh (Figma 6337:14738)
 *
 * The terminating "Você chegou ao Padrinho" row at the very bottom of the
 * Missões timeline. Renders three things:
 *   1. The current user's avatar (falls back to a person glyph if missing)
 *   2. A 20×20 sparkle/sun icon — the milestone-arrival glyph
 *   3. The arrival text + the formatted arrival date on the right
 *      (e.g. "20 Abr 26" in PT). Locale-aware month abbreviations.
 *
 * Pulls avatar + created_at from `useAuth()` so the callsite stays a
 * one-liner. Fully presentational beyond that — no fetches.
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Image } from 'expo-image';
import { Svg, Path } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

// --- 20×20 sparkle/sun glyph from Figma 1495:3693 -------------------------
const ArrivalSparkle = () => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    {/* 4 diagonal "ray" dashes at the corners */}
    <Path
      d="M4.46484 14.5857L5.89917 13.1505M15.0714 14.5857L13.6372 13.1505M5.10834 4.62256L5.89917 5.41336"
      stroke="#669AB7"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* 4 cardinal "ray" dashes (top, bottom, left, right) */}
    <Path
      d="M9.76684 3.21631V3.81446M9.76684 16.7858V14.7571M17.2668 9.28581H15.2383M2.73242 9.28581H4.29551"
      stroke="#669AB7"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// --- Locale-aware month abbreviations (3 chars, no trailing dot) ----------
const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function formatArrivalDate(iso: string | null | undefined, lang: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.getDate();
  const months = lang === 'en' ? MONTHS_EN : lang === 'es' ? MONTHS_ES : MONTHS_PT;
  const month = months[d.getMonth()];
  const year = String(d.getFullYear()).slice(-2);
  return `${day} ${month} ${year}`;
}

interface Props {
  /** Optional override for the avatar; defaults to the authed user's. */
  avatarUrl?: string | null;
  /** Optional override for the arrival date; defaults to profile.created_at. */
  arrivedAt?: string | null;
}

export default function JourneyOriginMarker({ avatarUrl, arrivedAt }: Props) {
  const { t, language } = useLanguage();
  const { userProfile } = useAuth();

  const resolvedAvatar = avatarUrl ?? userProfile?.avatar_url ?? null;
  const resolvedDate = arrivedAt ?? userProfile?.created_at ?? null;
  const dateLabel = formatArrivalDate(resolvedDate, language);

  return (
    <View style={styles.row}>
      {/* Avatar — 32×32, Blue Blumine fallback bg, Bege Cotton Seed border */}
      <View style={styles.avatarWrap}>
        {resolvedAvatar ? (
          <Image source={{ uri: resolvedAvatar }} style={styles.avatarImg} />
        ) : (
          <View style={styles.avatarPlaceholder}>
            <Svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <Path
                d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"
                stroke="#FFFFFF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
          </View>
        )}
      </View>

      {/* Right group — sparkle + text + date */}
      <View style={styles.contentRow}>
        <ArrivalSparkle />
        <Text style={styles.label} numberOfLines={1}>
          {t('missoes.origin.youArrivedAt')}
        </Text>
        {!!dateLabel && <Text style={styles.date}>{dateLabel}</Text>}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 8,
    width: '100%',
  },
  // 32×32 avatar with Bege Cotton Seed border + Blue Blumine fallback bg.
  avatarWrap: {
    width: 32,
    height: 32,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#C4C2BA',
    backgroundColor: '#015480',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImg: {
    width: 32,
    height: 32,
    borderRadius: 20,
  },
  avatarPlaceholder: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Right group — sparkle + label + date, gap 8.
  contentRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  label: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 14,
  },
  date: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 14,
  },
});
